import React, { useState, useEffect, lazy, Suspense } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { Box, CircularProgress, Icon, Fab, Modal } from "@mui/material";
import CssBaseline from "@mui/material/CssBaseline";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useMediaQuery } from "@mui/material";
import theme from "assets/theme";
import themeDark from "assets/theme-dark";
import routes from "routes";
import {
  useMaterialUIController,
  setMiniSidenav,
  setOpenConfigurator,
} from "context";
import { useAuth } from "layouts/authentication/AuthContext";
import axios from "axios";
import BASE_URL from "apiConfig";
import MDBox from "components/MDBox";
import Configurator from "examples/Configurator";
import axiosInstance from "apiConfig";
// Dynamic Sidenav logo fetching
import logo from "./assets/images/SalesHive.png";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import "./root.css";
const DashboardNavbar = React.lazy(() =>
  import("examples/Navbars/DashboardNavbar")
);
// Lazy load components
const Sidenav = React.lazy(() => import("examples/Sidenav"));
const LeadDetails = React.lazy(() => import("layouts/tables/Details"));

const SignIn = React.lazy(() => import("layouts/authentication/sign-in/index"));
const SignUp = React.lazy(() => import("layouts/authentication/sign-up/index"));
const Dashboard = React.lazy(() => import("layouts/dashboard"));

const Status = React.lazy(() => import("layouts/settings"));
const Reports = React.lazy(() => import("layouts/reports"));
const RMLeads = React.lazy(() => import("layouts/dashboard/RMLeads"));
const ProtectedRoute = React.lazy(() =>
  import("layouts/authentication/ProtectedRoute")
);

const ForgotPassword = React.lazy(() =>
  import("layouts/authentication/forgot-password/index")
);
const ResetPassword = React.lazy(() =>
  import("layouts/authentication/reset-password/index")
);
const Terms = React.lazy(() => import("layouts/Homepage/terms"));
const Privacy = React.lazy(() => import("layouts/Homepage/privacy"));
const Contact = React.lazy(() => import("layouts/Homepage/contact"));
const Unauthorized = React.lazy(() =>
  import("layouts/authentication/Unauthorized")
);
const ChatBot = React.lazy(() => import("layouts/chatbot"));

import { registerUser } from "layouts/authentication/socketService";
import { CurrencyProvider } from "context/CurrencyContext";
import BlogListPage from "layouts/blog";
import BlogPostPage from "layouts/blog/BlogPostPage";
import GlobalSearch from "examples/GlobalSearch";
import NewFeaturesAlert from "examples/NewFeaturesAlert";


export default function App() {
  // Match the sidebar's `< 1200` switch exactly. The former inclusive query
  // rendered both the mobile navbar and the desktop drawer at 1200px.
  const isMobile = useMediaQuery("(max-width:1199.95px)");
  const [controller, dispatch] = useMaterialUIController();
  const {
    miniSidenav,
    direction,
    layout,
    sidenavColor,
    transparentSidenav,
    whiteSidenav,
    openConfigurator,
    darkMode,
  } = controller;
  const [onMouseEnter, setOnMouseEnter] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // const [logo, setLogo] = useState(null); // State to store logo URL
  const { pathname } = useLocation();
  const { isAuthenticated,tenantId } = useAuth(); // Use Auth Context
  useEffect(() => {
    document.body.toggleAttribute("data-crm", isAuthenticated);
    return () => document.body.removeAttribute("data-crm");
  }, [isAuthenticated]);
  useEffect(() => {
    if (!isAuthenticated) return;
    const openSearch = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === "k") {
        event.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", openSearch);
    return () => window.removeEventListener("keydown", openSearch);
  }, [isAuthenticated]);
  const [isChatBotOpen, setIsChatBotOpen] = useState(false);

  useEffect(() => {
    const userId = localStorage.getItem("userId"); // Fetch user ID
    if (userId) {
      registerUser(userId); // Register user in WebSocket
    }
  }, []);

  const handleOnMouseEnter = () => {
    if (!isMobile && miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatch, false);
      setOnMouseEnter(true);
    }
  };

  const handleOnMouseLeave = () => {
    if (onMouseEnter) {
      setMiniSidenav(dispatch, true);
      setOnMouseEnter(false);
    }
  };
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleConfiguratorOpen = () =>
    setOpenConfigurator(dispatch, !openConfigurator);
  // Fetch logo from backend
  // useEffect(() => {
  //   const fetchLogo = async () => {
  //     try {
  //       const response = await axiosInstance.get("/settings");
  //       setLogo(response.data.logo_url);
  //     } catch (error) {
  //       console.error("Error fetching logo:", error);
  //     }
  //   };
  //   fetchLogo();
  // }, []);

  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  useEffect(() => {
    document.documentElement.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [pathname]);

  const getRoutes = (allRoutes) =>
    allRoutes.map((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route) {
        return (
          <Route
            exact
            path={route.route}
            element={route.component}
            key={route.key}
          />
        );
      }

      return null;
    });

  const configsButton = (
    <MDBox
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.25rem"
      height="3.25rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="small" color="inherit">
        settings
      </Icon>
    </MDBox>
  );

  const allRoutes = routes(); // Call the routes function to get the routes array

  return (
    <Suspense
      fallback={
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="100vh"
        >
          <CircularProgress />
        </Box>
      }
    >
      {/* <ThemeProvider theme={darkMode ? themeDark : theme}> */}
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />

        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={logo} // Pass the dynamic logo URL here
              routes={allRoutes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {/* <Configurator />
               {configsButton} */}
          </>
        )}
        {isAuthenticated && <DashboardNavbar onSearch={() => setSearchOpen(true)} searchOpen={searchOpen} />}
        {isAuthenticated && <NewFeaturesAlert />}
        {isAuthenticated && (
          <>
            <GlobalSearch open={searchOpen} onClose={() => setSearchOpen(false)} />
          </>
        )}
        <CurrencyProvider>
        <Routes>
          <Route path="/" element={<Navigate to={isAuthenticated ? "/dashboard" : "/signin"} replace />} />
          <Route path="/signin" element={<SignIn />} />
          <Route path="/register" element={<SignUp />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy-policy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/unauthorized" element={<Unauthorized />} />
          <Route path="/blog" element={<BlogListPage />} />
          <Route path="/blog/:slug" element={<BlogPostPage/>} />
          {isAuthenticated ? (
         
            <>

              {getRoutes(allRoutes)}
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/leads/:id" element={<LeadDetails />} />
       
           
              <Route
                path="/settings"
                element={
                  <ProtectedRoute allowedRoles={["Admin"]}>
                    <Status />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/reports"
                element={
                  <ProtectedRoute allowedRoles={["Admin"]}>
                    <Reports />
                  </ProtectedRoute>
                }
              />
             
             
            </>
          ) : (
            <Route path="*" element={<SignIn />} />
          )}
        </Routes>
          </CurrencyProvider>
        {!isAuthenticated && (
          <Fab
            color="info"
            aria-label="whatsapp"
            sx={{
              position: "fixed",
              bottom: 10,
              right: 16,
              zIndex: 1000,
              backgroundColor: "#25D366",
              "&:hover": {
                backgroundColor: "#20b954",
              },
            }}
            onClick={() => {
              window.open("https://wa.me/919599044483", "_blank");
            }}
          >
            <WhatsAppIcon fontSize="large" />
          </Fab>
        )}
        {/* <Fab
          color="primary"
          aria-label="chatbot"
          sx={{
            position: "fixed",
            bottom: 16,
            right: 16,
            zIndex: 1000,
          }}
          onClick={() => setIsChatBotOpen(true)}
        >
          <Icon>chat</Icon>
        </Fab> */}

        {/* Chatbot Modal */}
        <Modal
          open={isChatBotOpen}
          onClose={() => setIsChatBotOpen(false)}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              width: "90%",
              maxWidth: "400px",
              height: "70%",
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: 24,
              p: 2,
            }}
          >
            <ChatBot />
          </Box>
        </Modal>

        <ToastContainer />
      </ThemeProvider>
    </Suspense>
  );
}
